import { createTheme, Theme } from '@mui/material/styles';
import { CompanyBrand, DEFAULT_COMPANY_BRAND } from './brand/types';
import { buildBrandCssVars, darkenHex, withAlpha } from './brand/colorUtils';

export const createAppTheme = (brand: CompanyBrand = DEFAULT_COMPANY_BRAND): Theme => {
  const cssVars = buildBrandCssVars(brand.primaryColor, brand.shellBackgroundColor);

  return createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: cssVars['--brand-primary'],
        dark: cssVars['--brand-primary-hover'],
        light: cssVars['--brand-primary-10'],
        contrastText: cssVars['--brand-on-primary'],
      },
      background: {
        default: cssVars['--brand-shell-bg'],
        paper: 'rgba(255, 255, 255, 0.05)',
      },
      text: {
        primary: '#fff',
        secondary: 'rgba(255, 255, 255, 0.9)',
        disabled: 'rgba(255, 255, 255, 0.5)',
      },
      error: {
        main: '#f44336',
      },
      warning: {
        main: '#ff9800',
      },
      info: {
        main: '#2196f3',
      },
      success: {
        main: '#00d377',
      },
    },
    typography: {
      fontFamily: [
        '"Lato"',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      h1: { fontSize: '2rem', fontWeight: 700 },
      h2: { fontSize: '1.5rem', fontWeight: 600 },
      h3: { fontSize: '1.25rem', fontWeight: 600 },
      h4: { fontSize: '1.1rem', fontWeight: 600 },
      h5: { fontSize: '1rem', fontWeight: 500 },
      h6: { fontSize: '0.875rem', fontWeight: 500 },
      body1: { fontSize: '1rem' },
      body2: { fontSize: '0.875rem' },
    },
    shape: {
      borderRadius: 8,
    },
    spacing: 8,
    components: {
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            fontFamily: '"Lato", sans-serif',
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 16px',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            fontFamily: '"Lato", sans-serif',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 8,
            boxShadow: 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundColor: theme.palette.background.default,
            opacity: 1,
            backgroundImage: 'none',
          }),
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: '"Lato", sans-serif',
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: 8,
            borderRadius: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
          bar: {
            backgroundColor: cssVars['--brand-primary'],
            borderRadius: 4,
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          colorPrimary: {
            color: cssVars['--brand-primary'],
          },
        },
      },
    },
  });
};

// Backward-compatible default export for tests or direct imports.
const theme = createAppTheme();
export default theme;

export { darkenHex, withAlpha };
